<i18n
  src="@/locales/views/organization/partners/producers/new_producer.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    name="org_partners_producers_list_producers_path"
    :params="{
      organization_id: organization_id
    }"
  />
  <bread-crumbs :labels="[module_name]"></bread-crumbs>

  <alert-danger :errors_data="std_err" />

  <div v-if="load_general_data" class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title") }}
        </h3>
      </div>

      <div class="panel-body">
        <div class="space-y-6">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.organization_producer.producer_code") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.trim="organization_producer.producer_code"
                  :class="[
                    'input-text',
                    isInvalid('organization_producer.producer_code')
                  ]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.producer_code") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.trim="producer_code"
                  :class="['input-text', isInvalid('producer_code')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.lastname") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.trim="lastname"
                  :class="['input-text', isInvalid('lastname')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.name") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model.trim="name"
                  :class="['input-text', isInvalid('name')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.gender_id") }}
              </label>
              <div class="mt-3">
                <radio-form
                  v-model="gender_id"
                  name="gender_id"
                  :options="genders"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.producer.birth_date") }}
              </label>
              <div class="mt-3">
                <date-form v-model="birth_date" />
              </div>
            </div>
            <div class="sm:col-span-3">
              <div class="mt-3">
                <switch-form v-model="organization_producer.active" :label="$t('model.producer.organization_producer.active')" />
              </div>
            </div>
            <div class="sm:col-span-3">
              <div class="mt-3">
                <switch-form
                  v-model="organization_producer.formalized"
                  :label="$t('model.producer.organization_producer.formalized')"
                />
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <template
              v-for="(attribute, index) in attribute_producers"
              :key="attribute.id"
            >
              <div
                v-if="isBoolean(attribute.datatype_id)"
                class="sm:col-span-3 flex align-middle items-center"
              >
                <switch-form
                  v-model="producer_attributes[index].value"
                  :label="attribute.label"
                />
              </div>
              <div v-if="isString(attribute.datatype_id)" class="sm:col-span-3">
                <label class="label-form">
                  {{ attribute.label }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="producer_attributes[index].value"
                    :class="['input-text', isInvalid('')]"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button
            @click.prevent="goToListProducers()"
            :label="$t('labels.cancel_button')"
          />
          <span class="space-actions"></span>
          <success-button
            v-if="!proc_save_button"
            @click.prevent="create()"
            :label="$t('labels.save_button')"
          />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>

  <loading-message v-else />
</template>

<script>
import Requester from "@/views/organization/requesters/partners/producers/manage_producer.js";
import GenderMixin from "@/components/views/mixins/organization/genders.js";
import DatatypeMixin from "@/components/views/mixins/organization/datatypes.js";

export default {
  name: "OrgPartnersProducersNewProducer",
  mixins: [GenderMixin, DatatypeMixin],
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      attribute_producers: [],
      producer_attributes: [],
      producer_code: null,
      lastname: "",
      name: "",
      gender_id: null,
      birth_date: null,
      organization_producer: { producer_code: null, active: true, formalized: true }
    };
  },
  props: {
    organization_id: {}
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => this.loadData())
    );
  },
  methods: {
    loadData: async function () {
      await this.fetchAttributeProducers();
      this.load_general_data = true;
    },
    fetchAttributeProducers: async function () {
      let response = await Requester.fetchAttributeProducers(
        this.organization_id
      );

      this.attribute_producers = response.data;

      this.producer_attributes = this.attribute_producers.map(attr => {
        return {
          value: this.datatypeDefaultValue(attr),
          attribute_producer_id: attr.id,
          organization_id: this.organization_id
        };
      });
    },
    datatypeDefaultValue: function (attr) {
      if (this.isBoolean(attr.datatype_id)) return false;
      if (this.isString(attr.datatype_id)) return "";
    },
    create: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.createProducer(this.organization_id, {
          producer_code: this.producer_code,
          name: this.name,
          lastname: this.lastname,
          gender_id: this.gender_id,
          birth_date: this.birth_date,
          organization_producer_attributes: this.organization_producer,
          producer_attributes: this.producer_attributes
        });

        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowProducer(response.data.producer_id);
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
        this.proc_save_button = false;
      }
    },
    goToShowProducer: function (producer_id) {
      this.$router.push({
        name: "org_partners_producers_show_producer_path",
        params: {
          organization_id: this.organization_id,
          producer_id: producer_id
        }
      });
    },
    goToListProducers: function () {
      this.$router.push({
        name: "org_partners_producers_list_producers_path",
        params: {
          organization_id: this.organization_id
        }
      });
    }
  }
};
</script>
